import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Search, ChevronLeft, ChevronRight, X, Filter, Loader2, CheckSquare, Square, Pencil, Save, Sparkles, Trash2, AlertTriangle, Ban, Plus, Download, XCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "../components/app/AppLayout";
import { StatusPill } from "../components/app/StatusPill";
import { FacilityDrawer } from "../components/app/FacilityDrawer";
import { PlansMultiSelect } from "../components/app/PlansMultiSelect";
import { facilitiesApi, bulkApi, auth } from "../lib/api";
import { usePermissions } from "../lib/permissions";
import { TID } from "../constants/testIds";
import { toast } from "sonner";
import { getApiError } from "../lib/utils";

const useDebounce = (v, ms = 300) => {
  const [d, setD] = useState(v);
  useEffect(() => {
    const t = setTimeout(() => setD(v), ms);
    return () => clearTimeout(t);
  }, [v, ms]);
  return d;
};

const Chip = ({ active, onClick, children, testId }) => (
  <button
    data-testid={testId}
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
      active
        ? "border-slate-900 bg-slate-900 text-white"
        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
    }`}
  >
    {children}
  </button>
);

const Select = ({ value, onChange, options, placeholder, testId }) => {
  const isSet = !!value;
  return (
    <select
      data-testid={testId}
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      style={{ colorScheme: "light" }}
      className={`min-w-0 rounded-md border px-2.5 py-2 text-xs font-semibold outline-none transition-colors focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20 ${
        isSet
          ? "border-nmi-700 bg-nmi-50 text-nmi-900"
          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
      }`}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => {
        const [val, label] = typeof o === "string" ? [o, o] : [o.value, o.label];
        return <option key={val} value={val}>{label}</option>;
      })}
    </select>
  );
};

const StatBar = ({ meta }) => (
  <div className="flex items-center gap-3 overflow-x-auto text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 md:tracking-[0.2em] md:gap-4">
    <span className="whitespace-nowrap"><span className="text-slate-900 font-mono">{meta.total.toLocaleString()}</span> total</span>
    <span className="whitespace-nowrap text-emerald-700"><span className="font-mono">{meta.active}</span> active</span>
    <span className="whitespace-nowrap text-red-700"><span className="font-mono">{meta.suspended}</span> suspended</span>
    <span className="whitespace-nowrap text-slate-500"><span className="font-mono">{meta.categories?.length || 0}</span> categories</span>
  </div>
);

// Mobile card view for a facility
const MobileFacilityCard = ({ f, onClick }) => (
  <button
    data-testid={TID.facilityRow(f.facility_id)}
    onClick={onClick}
    className="w-full rounded-md border border-slate-200 bg-white p-3 text-left transition-colors hover:bg-slate-50 active:bg-slate-100"
  >
    <div className="flex items-start justify-between gap-2">
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm font-semibold text-slate-900">{f.name}</div>
        <div className="mt-0.5 flex items-center gap-1 text-[11px] text-slate-500">
          <span className="font-bold uppercase tracking-wider">{f.category}</span>
          <span>·</span>
          <span className="truncate">{f.town || "—"}</span>
        </div>
      </div>
      <StatusPill status={f.status} testId={`row-status-${f.facility_id}`} />
    </div>
    <div className="mt-2 flex items-center justify-between gap-2">
      <div className="min-w-0 truncate text-[11px] text-slate-500">
        {f.phones?.[0] || <span className="italic">no phone</span>}
      </div>
    </div>
  </button>
);

export default function Facilities() {
  const me = auth.me();
  const { has } = usePermissions();
  // Effective capability gates. Any user carrying the underlying permission
  // (via their role's preset — including CUSTOM roles) sees the affordance.
  const canEdit = has("hsp:facilities:edit");
  const canSuspend = has("hsp:facilities:suspend");
  const canReactivate = has("hsp:facilities:reactivate");
  const canDelete = has("hsp:facilities:delete");
  const canBulkStatus = canSuspend || canReactivate || has("hsp:facilities:terminate");
  const isAdmin = canEdit || canSuspend || canReactivate;  // "shows any admin action"
  const isSuperAdmin = canDelete;                          // "shows destructive action"
  void me;
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const dq = useDebounce(q, 250);
  const [category, setCategory] = useState(null);
  const [region, setRegion] = useState(null);
  const [district, setDistrict] = useState(null);
  const [town, setTown] = useState(null);
  const [statusF, setStatusF] = useState(null);
  const [plan, setPlan] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const stored = parseInt(localStorage.getItem("nmi_facilities_page_size") || "25", 10);
    return [25, 50, 100, 200].includes(stored) ? stored : 25;
  });
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [drawerId, setDrawerId] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkStatusOpen, setBulkStatusOpen] = useState(null); // 'suspend' | 'reactivate' | null
  const [addOpen, setAddOpen] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const navigate = useNavigate();

  // Persist page size choice
  useEffect(() => {
    try { localStorage.setItem("nmi_facilities_page_size", String(pageSize)); }
    catch (e) { console.warn("[Facilities] persist pageSize failed:", e); }
  }, [pageSize]);

  const toggleSel = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const allOnPageSelected = data.items.length > 0 && data.items.every((f) => selected.has(f.facility_id));
  const togglePageAll = () =>
    setSelected((s) => {
      const n = new Set(s);
      if (allOnPageSelected) data.items.forEach((f) => n.delete(f.facility_id));
      else data.items.forEach((f) => n.add(f.facility_id));
      return n;
    });

  const reloadMeta = () =>
    facilitiesApi.meta().then(setMeta).catch((err) => {
      console.error("[Facilities] meta load failed:", err?.response?.status, err?.response?.data || err.message);
    });

  useEffect(() => {
    reloadMeta();
  }, []);

  const params = useMemo(() => ({
    q: dq || undefined,
    category: category || undefined,
    region: region || undefined,
    district: district || undefined,
    town: town || undefined,
    status: statusF || undefined,
    plan: plan || undefined,
    page,
    page_size: pageSize,
  }), [dq, category, region, district, town, statusF, plan, page, pageSize]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await facilitiesApi.list(params);
      setData(d);
    } finally {
      setLoading(false);
    }
  }, [params]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [dq, category, region, district, town, statusF, plan, pageSize]);

  // Handler to select every facility_id matching current filters (across all pages)
  const selectAllMatching = async () => {
    if (selectingAll) return;
    setSelectingAll(true);
    try {
      const filterParams = {
        q: dq || undefined,
        category: category || undefined,
        region: region || undefined,
        district: district || undefined,
        town: town || undefined,
        status: statusF || undefined,
        plan: plan || undefined,
      };
      const res = await facilitiesApi.listIds(filterParams);
      const next = new Set(res.ids || []);
      setSelected(next);
      if (res.capped) {
        toast.warning(`Selected the first ${res.count} matching facilities (limit reached)`);
      } else {
        toast.success(`Selected ${res.count} facilities matching current filters`);
      }
    } catch (e) {
      toast.error(getApiError(e, "Couldn't fetch matching IDs"));
    } finally {
      setSelectingAll(false);
    }
  };

  // Stream the current filtered set as an Excel file. Uses the same filter
  // params as the visible table so users always download exactly what they see.
  const downloadFiltered = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const filterParams = {
        q: dq || undefined,
        category: category || undefined,
        region: region || undefined,
        district: district || undefined,
        town: town || undefined,
        status: statusF || undefined,
        plan: plan || undefined,
      };
      const res = await facilitiesApi.exportXlsx(filterParams);
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      const today = new Date().toISOString().slice(0, 10);
      a.download = `facilities-${today}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
      toast.success(`Downloaded ${data.total.toLocaleString()} facilit${data.total === 1 ? "y" : "ies"}`);
    } catch (e) {
      toast.error(getApiError(e, "Download failed. Please try again."));
    } finally {
      setDownloading(false);
    }
  };


  const activeFilters = [category, region, district, town, statusF, plan].filter(Boolean);

  const districtOptions = useMemo(() => {
    const opts = (meta?.districts || []).map((d) => ({ value: d, label: d }));
    const missing = meta?.missing_district_count || 0;
    if (missing > 0) {
      opts.unshift({
        value: "__none__",
        label: `— No district (${missing.toLocaleString()}) —`,
      });
    }
    return opts;
  }, [meta]);

  return (
    <AppLayout
      mobileTitle="Facilities"
      topbarExtra={
        <>
          <div className="relative w-full flex-1 md:max-w-xl">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid={TID.searchInput}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search name, town, or ID…"
              className="w-full rounded-md border border-slate-200 bg-slate-50 pl-9 pr-9 py-2 text-sm text-slate-900 outline-none transition-colors focus:border-nmi-600 focus:bg-white focus:ring-2 focus:ring-nmi-600/20"
            />
            {q && (
              <button
                onClick={() => setQ("")}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X size={14} />
              </button>
            )}
          </div>
          <div className="ml-auto hidden md:block">
            {meta && <StatBar meta={meta} />}
          </div>
        </>
      }
    >
      <div className="mb-4 md:mb-6 flex flex-wrap items-start justify-between gap-2 md:gap-4">
        <div>
          <div className="hidden md:block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            HSP Directory
          </div>
          <h1 className="hidden md:block font-heading text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Facilities
          </h1>
          <p className="hidden md:block mt-1 text-sm text-slate-600">
            Suspend, reactivate and inspect the {meta ? meta.total.toLocaleString() : "…"} accredited providers.
          </p>
          {/* Mobile stat bar */}
          <div className="md:hidden">
            {meta && <StatBar meta={meta} />}
          </div>
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          {isAdmin && (
            <button
              data-testid="add-facility-btn"
              onClick={() => setAddOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-2 text-xs font-semibold text-white hover:bg-nmi-800 shadow-sm"
            >
              <Plus size={13} strokeWidth={2.5} /> Add facility
            </button>
          )}
          <button
            data-testid="download-filtered-btn"
            onClick={downloadFiltered}
            disabled={downloading || loading || !data.total}
            title={data.total ? `Download ${data.total.toLocaleString()} matching facilities as Excel` : "No matching facilities to download"}
            className="inline-flex items-center gap-1.5 rounded-md border border-nmi-700 bg-white px-3 py-2 text-xs font-semibold text-nmi-800 hover:bg-nmi-50 shadow-sm disabled:cursor-not-allowed disabled:opacity-50"
          >
            {downloading ? <Loader2 size={13} className="animate-spin" strokeWidth={2.5} /> : <Download size={13} strokeWidth={2.5} />}
            Download{data.total ? ` · ${data.total.toLocaleString()}` : ""}
          </button>
        </div>
      </div>

      {/* Desktop filters */}
      <div className="mb-4 hidden flex-wrap items-center gap-2 md:flex">
        <Filter size={14} className="text-slate-400" />
        <Select testId={TID.filterCategory} value={category} onChange={setCategory} options={meta?.categories || []} placeholder="All facility types" />
        <Select testId={TID.filterRegion} value={region} onChange={setRegion} options={meta?.regions || []} placeholder="All regions" />
        <Select testId="filter-district-select" value={district} onChange={setDistrict} options={districtOptions} placeholder="All districts" />
        <Select testId="filter-town-select" value={town} onChange={setTown} options={meta?.towns || []} placeholder="All towns" />
        <Select testId={TID.filterStatus} value={statusF} onChange={setStatusF} options={["ACTIVE", "SUSPENDED", "TERMINATED"]} placeholder="All statuses" />
        <Select testId="filter-plan-select" value={plan} onChange={setPlan} options={meta?.plans || []} placeholder="All plans" />
        {activeFilters.length > 0 && (
          <button
            data-testid={TID.clearFilters}
            onClick={() => { setCategory(null); setRegion(null); setDistrict(null); setTown(null); setStatusF(null); setPlan(null); }}
            className="ml-1 text-[11px] font-semibold text-slate-500 underline hover:text-slate-900"
          >
            Clear
          </button>
        )}
        <div className="ml-auto text-[11px] font-medium text-slate-500">
          {loading ? "Loading…" : `${data.total.toLocaleString()} results`}
        </div>
      </div>

      {/* Mobile filter toggle */}
      <div className="mb-3 flex items-center gap-2 md:hidden">
        <button
          onClick={() => setShowFilters((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-md border px-3 py-2 text-xs font-medium transition-colors ${
            activeFilters.length > 0 || showFilters
              ? "border-slate-900 bg-slate-900 text-white"
              : "border-slate-200 bg-white text-slate-700"
          }`}
          data-testid="mobile-filter-toggle"
        >
          <Filter size={14} strokeWidth={2.5} />
          Filters {activeFilters.length > 0 && `· ${activeFilters.length}`}
        </button>
        {activeFilters.length > 0 && (
          <button
            onClick={() => { setCategory(null); setRegion(null); setDistrict(null); setTown(null); setStatusF(null); setPlan(null); }}
            className="text-[11px] font-semibold text-slate-500 underline"
          >
            Clear
          </button>
        )}
        <div className="ml-auto text-[11px] font-medium text-slate-500">
          {loading ? "…" : `${data.total.toLocaleString()}`}
        </div>
      </div>

      {showFilters && (
        <div className="mb-3 grid grid-cols-2 gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 md:hidden">
          <Select testId={TID.filterCategory} value={category} onChange={setCategory} options={meta?.categories || []} placeholder="Facility type" />
          <Select testId={TID.filterRegion} value={region} onChange={setRegion} options={meta?.regions || []} placeholder="Region" />
          <Select testId="filter-district-select-mobile" value={district} onChange={setDistrict} options={districtOptions} placeholder="District" />
          <Select testId="filter-town-select-mobile" value={town} onChange={setTown} options={meta?.towns || []} placeholder="Town" />
          <Select testId={TID.filterStatus} value={statusF} onChange={setStatusF} options={["ACTIVE", "SUSPENDED", "TERMINATED"]} placeholder="Status" />
          <Select testId="filter-plan-select-mobile" value={plan} onChange={setPlan} options={meta?.plans || []} placeholder="Plan" />
        </div>
      )}

      {/* Bulk action bar (admin only, when selected) */}
      {isAdmin && selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-3 rounded-md border border-nmi-200 bg-nmi-50 px-3 py-2">
          <div className="text-xs font-bold text-nmi-900">
            {selected.size} facilit{selected.size === 1 ? "y" : "ies"} selected
          </div>
          {/* Select-all-matching helper: show whenever there are more matching than we already picked */}
          {data.total > selected.size && (
            <button
              data-testid="bulk-select-all-matching"
              onClick={selectAllMatching}
              disabled={selectingAll}
              className="inline-flex items-center gap-1.5 rounded-md border border-nmi-700 bg-white px-2.5 py-1 text-[11px] font-semibold text-nmi-800 hover:bg-nmi-50 disabled:opacity-60"
              title="Select every facility that matches your current filters, across all pages"
            >
              {selectingAll ? <Loader2 size={11} className="animate-spin" /> : <CheckSquare size={11} strokeWidth={2.5} />}
              Select all {data.total.toLocaleString()} matching
            </button>
          )}
          <button
            data-testid={TID.bulkEditButton}
            onClick={() => setBulkOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-nmi-800"
          >
            <Pencil size={12} strokeWidth={2.5} /> Bulk edit
          </button>
          <button
            data-testid={TID.aiBulkEditButton}
            onClick={() => {
              try {
                localStorage.setItem("nmi_ai_edit_ids", JSON.stringify(Array.from(selected)));
              } catch (e) {
                // Non-sensitive UX handoff (facility ids already visible in URL/UI).
                // A quota or Safari-private failure just means AI Edit starts empty.
                console.warn("[facilities] failed to stash ai-edit selection:", e);
              }
              navigate("/ai-edit");
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-nmi-700 bg-white px-3 py-1.5 text-xs font-semibold text-nmi-800 hover:bg-nmi-50"
          >
            <Sparkles size={12} strokeWidth={2.5} /> AI Edit
          </button>
          {isAdmin && (
            <>
              <button
                data-testid="bulk-suspend-btn"
                onClick={() => setBulkStatusOpen("suspend")}
                className="inline-flex items-center gap-1.5 rounded-md border border-amber-500 bg-white px-3 py-1.5 text-xs font-semibold text-amber-800 hover:bg-amber-50"
              >
                <Ban size={12} strokeWidth={2.5} /> Bulk suspend
              </button>
              <button
                data-testid="bulk-reactivate-btn"
                onClick={() => setBulkStatusOpen("reactivate")}
                className="inline-flex items-center gap-1.5 rounded-md border border-emerald-500 bg-white px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-50"
              >
                <CheckSquare size={12} strokeWidth={2.5} /> Bulk reactivate
              </button>
              <button
                data-testid="bulk-terminate-btn"
                onClick={() => setBulkStatusOpen("terminate")}
                title="Mark selected as TERMINATED — permanent, they won't be suggested or shared again"
                className="inline-flex items-center gap-1.5 rounded-md border border-red-600 bg-white px-3 py-1.5 text-xs font-semibold text-red-800 hover:bg-red-50"
              >
                <XCircle size={12} strokeWidth={2.5} /> Bulk terminate
              </button>
            </>
          )}
          {isSuperAdmin && (
            <button
              data-testid="bulk-delete-btn"
              onClick={() => setBulkDeleteOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-md border border-red-600 bg-white px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50"
            >
              <Trash2 size={12} strokeWidth={2.5} /> Delete selected
            </button>
          )}
          <button
            onClick={() => setSelected(new Set())}
            className="ml-auto text-[11px] font-semibold text-nmi-700 underline hover:text-nmi-900"
          >
            Clear selection
          </button>
        </div>
      )}

      {/* Mobile cards */}
      <div className="space-y-2 md:hidden">
        {loading && (
          <div className="p-10 text-center text-slate-500">
            <Loader2 size={18} className="inline animate-spin" /> Loading…
          </div>
        )}
        {!loading && data.items.length === 0 && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-10 text-center text-sm text-slate-500">
            No facilities match your filters.
          </div>
        )}
        {!loading && data.items.map((f) => (
          <div key={f.facility_id} className="flex items-start gap-2">
            {isAdmin && (
              <button
                data-testid={TID.bulkSelectRow(f.facility_id)}
                onClick={() => toggleSel(f.facility_id)}
                className="mt-3 shrink-0 rounded p-1 text-slate-500 hover:bg-slate-100"
                aria-label="Select"
              >
                {selected.has(f.facility_id) ? <CheckSquare size={18} className="text-nmi-700" /> : <Square size={18} />}
              </button>
            )}
            <div className="flex-1">
              <MobileFacilityCard f={f} onClick={() => setDrawerId(f.facility_id)} />
            </div>
          </div>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden overflow-hidden rounded-md border border-slate-200 md:block">
        <table data-testid={TID.facilityTable} className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-slate-200 bg-slate-50">
              {isAdmin && (
                <th className="w-10 pl-4">
                  <button
                    data-testid={TID.bulkSelectAll}
                    onClick={togglePageAll}
                    className="rounded p-1 text-slate-500 hover:bg-slate-100"
                    aria-label="Select all"
                  >
                    {allOnPageSelected ? <CheckSquare size={16} className="text-nmi-700" /> : <Square size={16} />}
                  </button>
                </th>
              )}
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Facility</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Facility type</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Town / Region</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Contact</th>
              <th className="px-4 py-3 text-left text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Status</th>
              <th className="w-8" />
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={isAdmin ? 7 : 6} className="p-10 text-center text-slate-500">
                <Loader2 size={18} className="inline animate-spin" /> Loading facilities…
              </td></tr>
            )}
            {!loading && data.items.length === 0 && (
              <tr><td colSpan={isAdmin ? 7 : 6} className="p-10 text-center text-slate-500">
                No facilities match your filters.
              </td></tr>
            )}
            {!loading && data.items.map((f) => (
              <tr
                key={f.facility_id}
                data-testid={TID.facilityRow(f.facility_id)}
                onClick={(e) => {
                  // Don't open drawer if the click came from the checkbox
                  if (e.target.closest("[data-noopen]")) return;
                  setDrawerId(f.facility_id);
                }}
                className="cursor-pointer border-b border-slate-200 transition-colors last:border-b-0 hover:bg-slate-50"
              >
                {isAdmin && (
                  <td className="pl-4">
                    <button
                      data-testid={TID.bulkSelectRow(f.facility_id)}
                      data-noopen
                      onClick={(e) => { e.stopPropagation(); toggleSel(f.facility_id); }}
                      className="rounded p-1 text-slate-500 hover:bg-slate-100"
                      aria-label="Select facility"
                    >
                      {selected.has(f.facility_id) ? <CheckSquare size={16} className="text-nmi-700" /> : <Square size={16} />}
                    </button>
                  </td>
                )}
                <td className="px-4 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-semibold text-slate-900">{f.name}</div>
                    <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-slate-500">
                      <span className="rounded-sm bg-slate-100 px-1.5 py-0.5 font-mono">{f.facility_id}</span>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-700">{f.category}</span>
                </td>
                <td className="px-4 py-3">
                  <div className="text-sm text-slate-800">{f.town || "—"}</div>
                  <div className="text-[11px] text-slate-500">{f.region || "—"}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="font-mono text-[11px] text-slate-700">{f.phones?.[0] || "—"}</div>
                  {f.phones?.length > 1 && (<div className="text-[10px] text-slate-400">+{f.phones.length - 1} more</div>)}
                </td>
                <td className="px-4 py-3">
                  <StatusPill status={f.status} testId={`row-status-${f.facility_id}`} />
                </td>
                <td className="pr-4">
                  <button
                    data-testid={TID.openDrawer(f.facility_id)}
                    aria-label="Open facility"
                    className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-900"
                  >
                    <ChevronRight size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 text-xs text-slate-600">
        <div className="flex items-center gap-3">
          <div>
            Page <span className="font-mono font-bold text-slate-900">{data.page}</span> of{" "}
            <span className="font-mono font-bold text-slate-900">{data.pages || 1}</span>
          </div>
          <div className="hidden items-center gap-1.5 md:flex">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Per page</span>
            <select
              data-testid="page-size-select"
              value={pageSize}
              onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
              style={{ colorScheme: "light" }}
              className="rounded-md border border-slate-200 bg-white px-2 py-1.5 text-xs font-semibold text-slate-700 outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20"
            >
              {[25, 50, 100, 200].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-1">
          <button
            data-testid={TID.paginationPrev}
            disabled={page <= 1}
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            aria-label="Previous"
          >
            <ChevronLeft size={14} />
          </button>
          <button
            data-testid={TID.paginationNext}
            disabled={page >= (data.pages || 1)}
            onClick={() => setPage((p) => Math.min(data.pages, p + 1))}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40"
            aria-label="Next"
          >
            <ChevronRight size={14} />
          </button>
        </div>
      </div>

      {drawerId && (
        <FacilityDrawer
          facilityId={drawerId}
          onClose={() => setDrawerId(null)}
          onChanged={load}
        />
      )}

      {bulkOpen && (
        <BulkEditModal
          count={selected.size}
          onClose={() => setBulkOpen(false)}
          onDone={() => { setBulkOpen(false); setSelected(new Set()); load(); reloadMeta(); }}
          facilityIds={Array.from(selected)}
        />
      )}

      {bulkDeleteOpen && (
        <BulkDeleteModal
          count={selected.size}
          facilityIds={Array.from(selected)}
          onClose={() => setBulkDeleteOpen(false)}
          onDone={() => { setBulkDeleteOpen(false); setSelected(new Set()); load(); reloadMeta(); }}
        />
      )}

      {bulkStatusOpen && (
        <BulkStatusModal
          mode={bulkStatusOpen}
          count={selected.size}
          facilityIds={Array.from(selected)}
          onClose={() => setBulkStatusOpen(null)}
          onDone={() => { setBulkStatusOpen(null); setSelected(new Set()); load(); reloadMeta(); }}
        />
      )}

      {addOpen && (
        <AddFacilityModal
          meta={meta}
          onClose={() => setAddOpen(false)}
          onCreated={(f) => {
            setAddOpen(false);
            toast.success(`${f.name} added`);
            load();
            setDrawerId(f.facility_id);
          }}
        />
      )}
    </AppLayout>
  );
}

// ---------- Bulk edit modal ----------
const BULK_FIELDS = [
  { key: "region", label: "Region" },
  { key: "district", label: "District" },
  { key: "town", label: "Town" },
  { key: "location", label: "Location" },
  { key: "nmi_plans", label: "NMI Plans" },
  { key: "relationship_manager", label: "Relationship Manager" },
  { key: "working_hours", label: "Working Hours" },
  { key: "service_days", label: "Service Days" },
  { key: "holidays", label: "Holidays" },
  { key: "email", label: "Email" },
];

const BulkEditModal = ({ count, onClose, onDone, facilityIds }) => {
  const [chosen, setChosen] = useState({}); // {field: value}
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const patch = Object.fromEntries(
      Object.entries(chosen).filter(([, v]) => v !== undefined && v !== "")
    );
    if (Object.keys(patch).length === 0) {
      toast.error("Set at least one field to apply");
      return;
    }
    if (!window.confirm(`Apply changes to ${count} facilit${count === 1 ? "y" : "ies"}?`)) return;
    setBusy(true);
    try {
      const res = await bulkApi.updateFacilities(facilityIds, patch);
      toast.success(`Updated ${res.updated}, skipped ${res.skipped_no_changes}`);
      onDone();
    } catch (e) {
      toast.error(getApiError(e, "Bulk update failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 backdrop-blur-sm p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-2xl rounded-md border border-slate-200 bg-white shadow-2xl max-h-[92vh] flex flex-col">
        <div className="border-b border-slate-200 px-5 py-4">
          <div className="flex items-center gap-2">
            <Pencil size={18} className="text-nmi-700" strokeWidth={2.5} />
            <h3 className="font-heading text-base font-bold text-slate-900">Bulk edit {count} facilit{count === 1 ? "y" : "ies"}</h3>
          </div>
          <p className="mt-1 text-xs text-slate-600">
            Only fields you fill in will be applied. Empty fields are ignored (no data lost).
          </p>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {BULK_FIELDS.map((f) => (
              <div key={f.key} className={f.key === "nmi_plans" ? "md:col-span-2" : ""}>
                <label className="mb-1 block text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{f.label}</label>
                {f.key === "nmi_plans" ? (
                  <div data-testid={TID.bulkField(f.key)}>
                    <PlansMultiSelect
                      value={chosen[f.key] ?? ""}
                      onChange={(v) => setChosen((c) => ({ ...c, [f.key]: v ?? "" }))}
                      testIdPrefix="bulk-plan"
                    />
                  </div>
                ) : (
                  <input
                    data-testid={TID.bulkField(f.key)}
                    value={chosen[f.key] ?? ""}
                    onChange={(e) => setChosen((c) => ({ ...c, [f.key]: e.target.value }))}
                    className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-nmi-600 focus:ring-2 focus:ring-nmi-600/20"
                    placeholder="Leave empty to skip"
                  />
                )}
              </div>
            ))}
          </div>
          <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3 text-[11px] text-amber-900">
            <b>Heads up:</b> Bulk edit records a separate diff entry per facility in each edit history.
            Facility <b>status</b> and audit trail are never affected — use the individual Suspend/Reactivate action for that.
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            data-testid={TID.bulkCancel}
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            data-testid={TID.bulkSubmit}
            onClick={submit}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-nmi-800 disabled:opacity-60"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} strokeWidth={2.5} />}
            Apply to {count} {count === 1 ? "facility" : "facilities"}
          </button>
        </div>
      </div>
    </div>
  );
};


// ---------- Bulk delete modal ----------
const BulkDeleteModal = ({ count, facilityIds, onClose, onDone }) => {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await facilitiesApi.bulkDelete(facilityIds, reason.trim() || null);
      toast.success(`Deleted ${r.deleted} facilit${r.deleted === 1 ? "y" : "ies"}${r.not_found?.length ? ` · ${r.not_found.length} not found` : ""}`);
      onDone?.();
    } catch (e) {
      toast.error(getApiError(e, "Bulk delete failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => !busy && onClose()}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md overflow-hidden rounded-lg border-2 border-red-300 bg-white shadow-2xl"
        data-testid="bulk-delete-modal"
      >
        <div className="flex items-start gap-3 border-b border-slate-200 p-5">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-red-100">
            <AlertTriangle size={20} className="text-red-700" />
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-slate-900">Delete {count} facilit{count === 1 ? "y" : "ies"}?</h3>
            <p className="mt-1 text-xs text-slate-600">
              This <b>permanently removes</b> the selected provider{count === 1 ? "" : "s"}. A snapshot is kept in the
              audit log for reference, but the records will no longer appear anywhere in the app.
            </p>
          </div>
        </div>

        <div className="p-5">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">Reason (optional)</div>
          <input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Duplicate rows, decommissioned, wrong region…"
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-nmi-600"
            data-testid="bulk-delete-reason"
            disabled={busy}
            maxLength={500}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            onClick={onClose}
            disabled={busy}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="bulk-delete-cancel"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy}
            autoFocus
            data-testid="bulk-delete-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} strokeWidth={2.5} />}
            Yes, delete {count}
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------- Bulk suspend / reactivate / terminate modal ----------
const BulkStatusModal = ({ mode, count, facilityIds, onClose, onDone }) => {
  const isSuspend = mode === "suspend";
  const isTerminate = mode === "terminate";
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const r = reason.trim();
    if (!r) {
      toast.error("Reason is required");
      return;
    }
    setBusy(true);
    try {
      const targetStatus = isTerminate ? "TERMINATED" : isSuspend ? "SUSPENDED" : "ACTIVE";
      const verbPast = isTerminate ? "terminated" : isSuspend ? "suspended" : "reactivated";
      const res = await facilitiesApi.bulkStatus(facilityIds, targetStatus, r);
      const parts = [
        `${res.changed} ${verbPast}`,
        res.skipped_already_in_that_state ? `${res.skipped_already_in_that_state} already in state` : null,
        res.not_found?.length ? `${res.not_found.length} not found` : null,
        res.errors?.length ? `${res.errors.length} errors` : null,
      ].filter(Boolean).join(" · ");
      toast.success(parts);
      onDone?.();
    } catch (e) {
      toast.error(getApiError(e, "Bulk status change failed"));
    } finally {
      setBusy(false);
    }
  };

  const accent = isTerminate
    ? { ring: "border-red-400", bg: "bg-red-100", fg: "text-red-800", btn: "bg-red-700 hover:bg-red-800", label: "Yes, terminate" }
    : isSuspend
    ? { ring: "border-amber-300", bg: "bg-amber-100", fg: "text-amber-700", btn: "bg-amber-600 hover:bg-amber-700", label: "Yes, suspend" }
    : { ring: "border-emerald-300", bg: "bg-emerald-100", fg: "text-emerald-700", btn: "bg-emerald-600 hover:bg-emerald-700", label: "Yes, reactivate" };

  const title = isTerminate ? "Terminate" : isSuspend ? "Suspend" : "Reactivate";
  const helper = isTerminate
    ? "PERMANENT. Selected providers will be marked TERMINATED — they will not appear as alternatives, be shared, or be bulk-included again. Record is kept for audit only."
    : isSuspend
    ? "Selected providers will be marked SUSPENDED, alert-subscribed users will be notified in real time, and the reason will be attached to each record."
    : "Selected providers will be marked ACTIVE and appear in Telegram lookups again. Everyone with alerts on will be notified.";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => !busy && onClose()}>
      <div
        onClick={(e) => e.stopPropagation()}
        className={`w-full max-w-md overflow-hidden rounded-lg border-2 ${accent.ring} bg-white shadow-2xl`}
        data-testid={`bulk-${mode}-modal`}
      >
        <div className="flex items-start gap-3 border-b border-slate-200 p-5">
          <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${accent.bg}`}>
            {isTerminate
              ? <XCircle size={20} className={accent.fg} />
              : isSuspend ? <Ban size={20} className={accent.fg} />
              : <CheckSquare size={20} className={accent.fg} />}
          </div>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-slate-900">
              {title} {count} facilit{count === 1 ? "y" : "ies"}?
            </h3>
            <p className="mt-1 text-xs text-slate-600">
              {helper}
            </p>
          </div>
        </div>

        <div className="p-5">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
            Reason (required) <span className="text-red-500">*</span>
          </div>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={isSuspend
              ? "e.g. Not accepting NMI members from 15 July pending renegotiation…"
              : "e.g. Contract renewed, back on the network as of today…"}
            className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm outline-none focus:border-nmi-600"
            data-testid={`bulk-${mode}-reason`}
            rows={3}
            disabled={busy}
            maxLength={500}
            autoFocus
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            onClick={onClose}
            disabled={busy}
            data-testid={`bulk-${mode}-cancel`}
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !reason.trim()}
            data-testid={`bulk-${mode}-submit`}
            className={`inline-flex items-center gap-1.5 rounded-md ${accent.btn} px-4 py-2 text-xs font-bold uppercase tracking-wider text-white disabled:opacity-40`}
          >
            {busy ? <Loader2 size={12} className="animate-spin" />
              : isSuspend ? <Ban size={12} strokeWidth={2.5} />
              : <CheckSquare size={12} strokeWidth={2.5} />}
            {accent.label} {count}
          </button>
        </div>
      </div>
    </div>
  );
};


// ---------- Add Facility (from scratch) modal ----------
const AddFacilityModal = ({ meta, onClose, onCreated }) => {
  const [form, setForm] = useState({
    name: "",
    category: "",
    region: "",
    district: "",
    town: "",
    location: "",
    phones: "",
    email: "",
    working_hours: "",
    maps_link: "",
    nmi_plans: "",
    relationship_manager: "",
    ghana_post_address: "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (!form.name.trim() || !form.category.trim()) {
      toast.error("Name and Facility type are required");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        ...form,
        phones: form.phones
          ? form.phones.split(/[,;\n]/).map((s) => s.trim()).filter(Boolean)
          : [],
      };
      const created = await facilitiesApi.create(payload);
      onCreated?.(created);
    } catch (e) {
      toast.error(getApiError(e, "Create failed"));
    } finally {
      setBusy(false);
    }
  };

  const Field = ({ label, k, required, placeholder, options }) => (
    <div>
      <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">
        {label}{required && <span className="text-red-500"> *</span>}
      </div>
      {options ? (
        <select
          value={form[k]}
          onChange={(e) => set(k, e.target.value)}
          disabled={busy}
          style={{ colorScheme: "light" }}
          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-nmi-600"
        >
          <option value="">— select —</option>
          {options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          value={form[k]}
          onChange={(e) => set(k, e.target.value)}
          disabled={busy}
          placeholder={placeholder}
          className="w-full rounded-md border border-slate-200 bg-white px-2 py-1.5 text-sm outline-none focus:border-nmi-600"
        />
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4" onClick={() => !busy && onClose()}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl overflow-hidden rounded-lg border border-slate-200 bg-white shadow-2xl max-h-[90vh] flex flex-col"
        data-testid="add-facility-modal"
      >
        <div className="flex items-start justify-between border-b border-slate-200 p-5">
          <div>
            <h3 className="font-heading text-lg font-bold text-slate-900">Add a new facility</h3>
            <p className="mt-1 text-xs text-slate-500">
              A 12-hex ID is generated automatically. Fields with * are required; everything else can be added later from the drawer.
            </p>
          </div>
          <button onClick={onClose} disabled={busy} className="rounded-md p-1.5 text-slate-500 hover:bg-slate-100"><X size={18} /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div className="md:col-span-2"><Field label="Facility name" k="name" required placeholder="e.g. Korle Bu Teaching Hospital" /></div>
            <Field label="Facility type" k="category" required options={meta?.categories || ["HOSPITAL", "CLINIC", "PHARMACY", "DENTAL", "DIAGNOSTIC", "OPTICAL", "MATERNITY", "HERBAL", "ENT"]} />
            <Field label="Region" k="region" options={meta?.regions || []} />
            <Field label="District" k="district" placeholder="e.g. Ablekuma South" />
            <Field label="Town / City" k="town" placeholder="e.g. Accra" />
            <div className="md:col-span-2"><Field label="Location / Suburb" k="location" placeholder="e.g. Korle-Bu" /></div>
            <div className="md:col-span-2"><Field label="Google Maps link" k="maps_link" placeholder="https://maps.app.goo.gl/…" /></div>
            <Field label="Ghana Post GPS" k="ghana_post_address" placeholder="GA-000-0000" />
            <Field label="Working hours" k="working_hours" placeholder="Mon–Fri 08:00–17:00 · 24/7" />
            <div className="md:col-span-2"><Field label="Phone numbers" k="phones" placeholder="+233… , +233…" /></div>
            <div className="md:col-span-2"><Field label="Email" k="email" placeholder="info@facility.gh" /></div>
            <div className="md:col-span-2">
              <Field label="Plans accepted (comma-separated)" k="nmi_plans" placeholder="PREMIER, STANDARD, CLASSIC" />
            </div>
            <div className="md:col-span-2"><Field label="Relationship manager" k="relationship_manager" placeholder="e.g. Yaw Boakye" /></div>
          </div>
        </div>
        <div className="flex justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            onClick={onClose}
            disabled={busy}
            data-testid="add-facility-cancel"
            className="rounded-md border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={busy || !form.name.trim() || !form.category.trim()}
            data-testid="add-facility-submit"
            className="inline-flex items-center gap-1.5 rounded-md bg-nmi-700 px-4 py-2 text-xs font-bold uppercase tracking-wider text-white hover:bg-nmi-800 disabled:opacity-40"
          >
            {busy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} strokeWidth={2.5} />}
            Create facility
          </button>
        </div>
      </div>
    </div>
  );
};

